import React, { useState } from 'react';
import { Badge, Switch, FormControlLabel, CircularProgress } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import {
  useGetFeaturesListQuery,
  useUpdateProviderFeatureFlagMutation,
} from '../store/api';

interface ProviderFeatureFlagsProps {
  providerId: string;
  currentFeatureFlags?: { [key: string]: any } | null;
}

export function ProviderFeatureFlags({
  providerId,
  currentFeatureFlags = {},
}: ProviderFeatureFlagsProps) {
  const { data: featuresList = [], isLoading: loadingFeatures } = useGetFeaturesListQuery();
  const [updateFeatureFlag, { isLoading: updating }] = useUpdateProviderFeatureFlagMutation();

  // Track optimistic updates - never clear them, just let them override
  const [optimisticFlags, setOptimisticFlags] = useState<{ [key: string]: boolean }>({});

  // Combine features from the list AND user's current flags
  const allFeatures = React.useMemo(() => {
    const featuresSet = new Set([
      ...featuresList,
      ...Object.keys(currentFeatureFlags || {}),
    ]);
    return Array.from(featuresSet).sort();
  }, [featuresList, currentFeatureFlags]);

  const handleToggle = async (featureName: string, currentValue: boolean, event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent double-toggling
    event.stopPropagation();

    const newValue = !currentValue;

    // Set optimistic update
    setOptimisticFlags(prev => ({ ...prev, [featureName]: newValue }));

    try {
      await updateFeatureFlag({
        providerId,
        featureName,
        featureValue: newValue,
      }).unwrap();

      // Success - keep the optimistic flag as the source of truth
      // It will stay until the component unmounts or user toggles again
    } catch (error) {
      console.error('Failed to update feature flag:', error);
      // On error, revert to the original value
      setOptimisticFlags(prev => {
        const { [featureName]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  if (loadingFeatures) {
    return (
      <div className="flex items-center justify-center p-4">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!allFeatures.length) {
    return (
      <div className="p-4 text-gray-500 text-sm">
        No feature flags available
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {allFeatures.map((feature: string) => {
        // Use optimistic value if available, otherwise use current value
        const isEnabled = optimisticFlags[feature] ?? currentFeatureFlags?.[feature] ?? false;

        return (
          <div
            key={feature}
            className={`flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${
              isEnabled ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3 flex-1">
              <Switch
                checked={isEnabled}
                onChange={(e) => handleToggle(feature, isEnabled, e)}
                disabled={updating}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#10b981',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#10b981',
                  },
                }}
              />
              <span className={`text-sm font-mono flex-1 ${isEnabled ? 'text-green-900 font-medium' : 'text-gray-600'}`}>
                {feature}
              </span>
            </div>
            <div className="flex items-center">
              {isEnabled ? (
                <Badge
                  badgeContent={<Check sx={{ fontSize: 10 }} />}
                  color="success"
                  sx={{
                    '& .MuiBadge-badge': {
                      minWidth: 18,
                      height: 18,
                      borderRadius: '50%',
                      fontSize: '0.625rem',
                    },
                  }}
                >
                  <span className="sr-only">Enabled</span>
                </Badge>
              ) : (
                <Badge
                  badgeContent={<Close sx={{ fontSize: 10 }} />}
                  sx={{
                    '& .MuiBadge-badge': {
                      minWidth: 18,
                      height: 18,
                      borderRadius: '50%',
                      fontSize: '0.625rem',
                      backgroundColor: '#9ca3af',
                    },
                  }}
                >
                  <span className="sr-only">Disabled</span>
                </Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
